/**
=========================================================
* Registration Form Component
=========================================================
*/

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkIcon from "@mui/icons-material/Link";
import SportsIcon from "@mui/icons-material/Sports";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CommentIcon from "@mui/icons-material/Comment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// API service
import { publicRegistrationAPI } from "services/api";

// Utility functions
import { generateCookieUserId, saveToLocalStorage, loadFromLocalStorage } from "./utils";

function RegistrationForm({ seriesData, suggestions, publicRegistrationCode, onSuccess }) {
  // Form state
  const [formData, setFormData] = useState({
    req_player_name: "",
    req_mobile_no: "",
    cric_heroes_profile_link: "",
    role: "",
    batting_style: "",
    bowling_style: "",
    keeping: "",
    jersey_no: "",
    jersey_name: "",
    jersey_size: "",
    jersey_lower_size: "",
    district_name: "",
    previous_team_details: "",
    comments: "",
    designation: "",
    office_location: "",
    job_type: "",
    foreign_player_details: "",
  });

  // File uploads
  const [profilePic, setProfilePic] = useState(null);
  const [gpayScreenshot, setGpayScreenshot] = useState(null);
  const [aadharFrontPic, setAadharFrontPic] = useState(null);
  const [aadharBackPic, setAadharBackPic] = useState(null);
  const [officeIdPhoto, setOfficeIdPhoto] = useState(null);
  // Stored base64 (for quick fill without a File object)
  const [storedProfilePicBase64, setStoredProfilePicBase64] = useState("");
  const [profilePreview, setProfilePreview] = useState("");
  const [gpayScreenshotPreview, setGpayScreenshotPreview] = useState("");
  const [aadharFrontPreview, setAadharFrontPreview] = useState("");
  const [aadharBackPreview, setAadharBackPreview] = useState("");
  const [officeIdPhotoPreview, setOfficeIdPhotoPreview] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasStoredData, setHasStoredData] = useState(false);
  
  // Registration timing state
  const [registrationStatus, setRegistrationStatus] = useState("open");
  const [countdownText, setCountdownText] = useState("");

  // Load stored data on component mount
  useEffect(() => {
    const storedData = loadFromLocalStorage();
    if (storedData) {
      setHasStoredData(true);
    }
  }, []);

  // Check registration timing and update countdown
  useEffect(() => {
    const checkRegistrationTiming = () => {
      const status = seriesData?.registration_status || "open";
      setRegistrationStatus(status);

      if (status === "not_started" && seriesData?.time_until_start) {
        setCountdownText(seriesData.time_until_start);
      } else {
        setCountdownText("");
      }
    };

    checkRegistrationTiming();
    
    // Update countdown every minute
    const interval = setInterval(checkRegistrationTiming, 60000);
    
    // Auto-refresh when registration opens (if currently not started)
    if (registrationStatus === "not_started") {
      const checkIfOpened = setInterval(() => {
        const now = new Date().getTime();
        const startTime = seriesData?.registration_start_date_time;
        if (startTime) {
          const startTimeMs = new Date(startTime.replace(" ", "T")).getTime();
          if (now >= startTimeMs) {
            window.location.reload();
          }
        }
      }, 30000); // Check every 30 seconds
      
      return () => {
        clearInterval(interval);
        clearInterval(checkIfOpened);
      };
    }
    
    return () => clearInterval(interval);
  }, [seriesData, registrationStatus]);

  // Handle Select icon clicks to open dropdown
  useEffect(() => {
    const handleIconClick = (e) => {
      const icon = e.target.closest('.MuiSelect-icon');
      if (icon) {
        // Find the parent Select component
        const selectRoot = icon.closest('.MuiOutlinedInput-root');
        if (selectRoot) {
          // Find the actual Select input element
          const selectElement = selectRoot.querySelector('[role="combobox"]') || 
                              selectRoot.querySelector('.MuiSelect-select') ||
                              selectRoot.querySelector('input');
          
          if (selectElement) {
            e.preventDefault();
            e.stopPropagation();
            // Focus and click the select element
            selectElement.focus();
            // Create and dispatch a click event
            const clickEvent = new MouseEvent('mousedown', {
              bubbles: true,
              cancelable: true,
              view: window
            });
            selectElement.dispatchEvent(clickEvent);
            // Also trigger a click
            setTimeout(() => {
              selectElement.click();
            }, 0);
          }
        }
      }
    };

    // Use capture phase to catch clicks early
    document.addEventListener('click', handleIconClick, true);
    
    return () => {
      document.removeEventListener('click', handleIconClick, true);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle file uploads
  const handleFileUpload = (field, file) => {
    if (!file) return;

    // Validate file type (images only)
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setError(`Please upload a valid image file (JPEG, PNG, GIF, or WebP)`);
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError(`File size must be less than 10MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    // Clear any previous errors
    setError(null);

    if (field === "profile_pic") {
      setProfilePic(file);
      // Once user selects a new file, ignore stored base64
      setStoredProfilePicBase64("");
    } else if (field === "gpay_screenshot") {
      setGpayScreenshot(file);
    } else if (field === "aadhar_front_pic") {
      setAadharFrontPic(file);
    } else if (field === "aadhar_back_pic") {
      setAadharBackPic(file);
    } else if (field === "office_id_photo") {
      setOfficeIdPhoto(file);
    }
  };

  // Quick fill from stored data
  const handleQuickFill = () => {
    const storedData = loadFromLocalStorage();
    if (storedData) {
      setFormData(prev => ({
        ...prev,
        ...storedData
      }));
      // Load stored profile picture base64 if present
      if (storedData.profile_pic_base64) {
        setStoredProfilePicBase64(storedData.profile_pic_base64);
      }
      setHasStoredData(false);
    }
  };

  // Maintain preview image URL (object URL or stored base64)
  useEffect(() => {
    let objectUrl = "";
    if (profilePic) {
      objectUrl = URL.createObjectURL(profilePic);
      setProfilePreview(objectUrl);
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }
    if (storedProfilePicBase64) {
      setProfilePreview(storedProfilePicBase64);
    } else {
      setProfilePreview("");
    }
  }, [profilePic, storedProfilePicBase64]);

  // Maintain GPay screenshot preview
  useEffect(() => {
    if (gpayScreenshot) {
      const objectUrl = URL.createObjectURL(gpayScreenshot);
      setGpayScreenshotPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setGpayScreenshotPreview("");
    }
  }, [gpayScreenshot]);

  // Maintain Aadhar front preview
  useEffect(() => {
    if (aadharFrontPic) {
      const objectUrl = URL.createObjectURL(aadharFrontPic);
      setAadharFrontPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setAadharFrontPreview("");
    }
  }, [aadharFrontPic]);

  // Maintain Aadhar back preview
  useEffect(() => {
    if (aadharBackPic) {
      const objectUrl = URL.createObjectURL(aadharBackPic);
      setAadharBackPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setAadharBackPreview("");
    }
  }, [aadharBackPic]);

  // Maintain Office ID photo preview
  useEffect(() => {
    if (officeIdPhoto) {
      const objectUrl = URL.createObjectURL(officeIdPhoto);
      setOfficeIdPhotoPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setOfficeIdPhotoPreview("");
    }
  }, [officeIdPhoto]);

  // Scroll to error message
  const scrollToError = () => {
    setTimeout(() => {
      const errorElement = document.querySelector('.MuiAlert-root');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Form validation
  const validateForm = () => {
    const errors = [];

    if (!formData.req_player_name.trim()) {
      errors.push("Player name is required");
    }

    if (!formData.req_mobile_no.trim()) {
      errors.push("Mobile number is required");
    } else if (!/^\d{10}$/.test(formData.req_mobile_no.replace(/\D/g, ""))) {
      errors.push("Please enter a valid 10-digit mobile number");
    }

    // Conditional validations based on series settings
    // if (seriesData?.show_cric_heroes_profile_link === "1" && !formData.cric_heroes_profile_link.trim()) {
    //   errors.push("Cric Heroes profile link is required");
    // }

    if (seriesData?.show_batting_style_etc === "1") {
      if (!formData.role) {
        errors.push("Role is required");
      }
      if (!formData.batting_style) {
        errors.push("Batting style is required");
      }
      if (!formData.bowling_style) {
        errors.push("Bowling style is required");
      }
    }

    if (seriesData?.show_jersey_no === "1" && !formData.jersey_no.trim()) {
      errors.push("Jersey number is required");
    }

    if (seriesData?.show_jersey_lower_size === "1" || seriesData?.show_jersey_lower_size === 1) {
      if (!formData.jersey_lower_size || !formData.jersey_lower_size.trim()) {
        errors.push("Jersey lower size (pant size) is required");
      }
    }

    if (!profilePic && !storedProfilePicBase64) {
      errors.push("Profile picture is required");
    }

    if (seriesData?.show_gpay_screenshot === "1" && !gpayScreenshot) {
      errors.push("GPay screenshot is required");
    }

    if (seriesData?.show_aadhar_pic === "1") {
      if (!aadharFrontPic) {
        errors.push("Aadhar front picture is required");
      }
      if (!aadharBackPic) {
        errors.push("Aadhar back picture is required");
      }
    }

    if (seriesData?.show_previous_team_details === "1" && !formData.previous_team_details.trim()) {
      errors.push("Current club name is required");
    }

    if (seriesData?.show_district === "1" && !formData.district_name) {
      errors.push("District is required");
    }

    // New field validations based on settings
    if (seriesData?.show_designation === "1" || seriesData?.show_designation === 1) {
      if (!formData.designation.trim()) {
        errors.push("Designation is required");
      }
    }

    if (seriesData?.show_office_location === "1" || seriesData?.show_office_location === 1) {
      if (!formData.office_location.trim()) {
        errors.push("Office location is required");
      }
    }

    if (seriesData?.show_job_type === "1" || seriesData?.show_job_type === 1) {
      if (!formData.job_type) {
        errors.push("Job type is required");
      }
    }

    if (seriesData?.show_office_id_photo === "1" || seriesData?.show_office_id_photo === 1) {
      if (!officeIdPhoto) {
        errors.push("Office ID photo is required");
      }
    }

    if (seriesData?.show_foreign_player_details === "1" || seriesData?.show_foreign_player_details === 1) {
      if (!formData.foreign_player_details.trim()) {
        errors.push("Foreign Player (RO) is required");
      }
    }

    if (formData.foreign_player_details && formData.foreign_player_details.length > 100) {
      errors.push("Foreign Player (RO) must be 100 characters or less");
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(", "));
      scrollToError();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Generate cookie user ID
      const cookieUserId = generateCookieUserId();

      // Prepare form data
      const submitData = {
        public_registration_code: publicRegistrationCode,
        cookie_user_id: cookieUserId,
        ...formData,
        // Trim foreign_player_details if present
        foreign_player_details: formData.foreign_player_details ? formData.foreign_player_details.trim() : "",
      };

      // Convert files to base64 strings if present
      const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      let profilePicBase64 = '';
      let gpayScreenshotBase64 = '';
      let aadharFrontPicBase64 = '';
      let aadharBackPicBase64 = '';
      let officeIdPhotoBase64 = '';

      if (profilePic) {
        try {
          profilePicBase64 = await fileToBase64(profilePic);
        } catch (err) {
          console.error('Failed to encode profile pic:', err);
        }
      }

      if (gpayScreenshot) {
        try {
          gpayScreenshotBase64 = await fileToBase64(gpayScreenshot);
        } catch (err) {
          console.error('Failed to encode gpay screenshot:', err);
        }
      }

      if (aadharFrontPic) {
        try {
          aadharFrontPicBase64 = await fileToBase64(aadharFrontPic);
        } catch (err) {
          console.error('Failed to encode aadhar front pic:', err);
        }
      }

      if (aadharBackPic) {
        try {
          aadharBackPicBase64 = await fileToBase64(aadharBackPic);
        } catch (err) {
          console.error('Failed to encode aadhar back pic:', err);
        }
      }

      if (officeIdPhoto) {
        try {
          officeIdPhotoBase64 = await fileToBase64(officeIdPhoto);
        } catch (err) {
          console.error('Failed to encode office ID photo:', err);
        }
      }

      const payload = {
        ...submitData,
        profile_pic: profilePicBase64 || storedProfilePicBase64 || '',
        gpay_screenshot: gpayScreenshotBase64 || '',
        aadhar_front_pic: aadharFrontPicBase64 || '',
        aadhar_back_pic: aadharBackPicBase64 || '',
        office_id_photo: officeIdPhotoBase64 || ''
      };

      // Submit registration (as JSON)
      const response = await publicRegistrationAPI.registerParticipant(payload);

      if (response.data.status === 1) {
        // Save to local storage for future quick fill
        saveToLocalStorage({
          ...formData,
          // Store profile pic as base64 only; do not store gpay screenshot
          profile_pic_base64: profilePicBase64 || storedProfilePicBase64 || ''
        });
        // Pass reference information to onSuccess callback
        onSuccess({
          ref_name: response.data.ref_name || null,
          ref_mobile_no: response.data.ref_mobile_no || null,
          req_series_participant_id: response.data.req_series_participant_id || null
        });
      } else {
        setError(response.data.message || "Registration failed");
        scrollToError();
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Registration failed. Please try again.");
      scrollToError();
    } finally {
      setLoading(false);
    }
  };

  // Clear form
  const handleClearForm = () => {
    setFormData({
      req_player_name: "",
      req_mobile_no: "",
      cric_heroes_profile_link: "",
      role: "",
      batting_style: "",
      bowling_style: "",
      keeping: "",
      jersey_no: "",
      jersey_name: "",
      jersey_size: "",
      jersey_lower_size: "",
    district_name: "",
    previous_team_details: "",
    comments: "",
    designation: "",
    office_location: "",
    job_type: "",
    foreign_player_details: "",
  });
    setProfilePic(null);
    setGpayScreenshot(null);
    setAadharFrontPic(null);
    setAadharBackPic(null);
    setOfficeIdPhoto(null);
    setError(null);
  };

  // Check if registration is not started
  const isNotStarted = registrationStatus === "not_started";
  const isClosed = registrationStatus === "closed";

  return (
    <Card 
      elevation={6}
      sx={{
        backgroundColor: "#fafbfc",
        background: "linear-gradient(135deg, #ffffff 0%, #f5f7fa 50%, #f0f2f5 100%)",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <SoftTypography variant="h4" color="dark" fontWeight="bold" mb={3}>
          Player Registration (പ്ലേയർ രജിസ്ട്രേഷൻ)
        </SoftTypography>

        {/* Registration Not Started Alert */}
        {isNotStarted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={1}>
              Registration Not Yet Open
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Registration will open {seriesData?.formatted_start_time && `on ${seriesData.formatted_start_time}`}
            </SoftTypography>
            {countdownText && (
              <SoftTypography variant="body2" color="text" fontWeight="bold" mt={1}>
                Time remaining: {countdownText}
              </SoftTypography>
            )}
            <SoftTypography variant="caption" color="text" display="block" mt={2}>
              This page will automatically refresh when registration opens.
            </SoftTypography>
          </Alert>
        )}

        {/* Registration Closed Alert */}
        {isClosed && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={1}>
              Registration Closed
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {seriesData?.registration_message || "Registration for this series has closed."}
            </SoftTypography>
          </Alert>
        )}

        {/* Blur overlay for not started or closed status */}
        <Box
          sx={{
            position: "relative",
            filter: (isNotStarted || isClosed) ? "blur(4px)" : "none",
            pointerEvents: (isNotStarted || isClosed) ? "none" : "auto",
            userSelect: (isNotStarted || isClosed) ? "none" : "auto",
          }}
        >
          {hasStoredData && !isNotStarted && !isClosed && 1===2 && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <SoftButton
                size="small"
                color="info"
                variant="outlined"
                onClick={handleQuickFill}
                startIcon={<RefreshIcon />}
              >
                Quick Fill
              </SoftButton>
            }
          >
            We found your previous registration data. Click "Quick Fill" to auto-populate the form.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                Basic Information (അടിസ്ഥാന വിവരങ്ങൾ)
              </SoftTypography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <SoftBox mb={2}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="dark"
                  sx={{ mb: 1, display: "block" }}
                >
                  Player Name (പേര്) *
                </SoftTypography>
                <SoftInput
                  placeholder="Enter your full name (നിങ്ങളുടെ പൂർണ്ണ നാമം നൽകുക)"
                  value={formData.req_player_name}
                  onChange={(e) => handleInputChange("req_player_name", e.target.value)}
                  icon={{
                    component: <PersonIcon />,
                    direction: "left",
                  }}
                  sx={{ height: "42px" }}
                />
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <SoftBox mb={2}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="dark"
                  sx={{ mb: 1, display: "block" }}
                >
                  Mobile Number (മൊബൈൽ നമ്പർ) *
                </SoftTypography>
                <SoftInput
                  placeholder="Enter 10-digit mobile number (10 അക്ക മൊബൈൽ നമ്പർ നൽകുക)"
                  value={formData.req_mobile_no}
                  onChange={(e) => handleInputChange("req_mobile_no", e.target.value)}
                  inputProps={{ maxLength: 10 }}
                  icon={{
                    component: <PhoneIcon />,
                    direction: "left",
                  }}
                  sx={{ height: "42px" }}
                />
              </SoftBox>
            </Grid>

            {/* District - Conditionally Required */}
            {seriesData?.show_district === "1" && (
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                    District (ജില്ല) *
                  </SoftTypography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.district_name}
                      onChange={(e) => handleInputChange("district_name", e.target.value)}
                      displayEmpty
                      sx={{
                        height: "42px",
                        paddingRight: "32px !important",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.87)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                        "& .MuiSelect-icon": {
                          display: "block !important",
                          visibility: "visible !important",
                          opacity: "1 !important",
                          fontSize: "2rem !important",
                          color: "#000000 !important",
                          right: "8px !important",
                          top: "calc(50% - 0.5em) !important",
                          position: "absolute !important",
                          pointerEvents: "auto !important",
                          cursor: "pointer !important",
                          zIndex: 1,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select district</em>
                      </MenuItem>
                      {seriesData?.district_option_values?.split(',').map((district, index) => (
                        <MenuItem key={index} value={district.trim()}>
                          {district.trim()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </SoftBox>
              </Grid>
            )}

            {/* Cric Heroes Profile Link */}
            {seriesData?.show_cric_heroes_profile_link === "1" && (
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Cric Heroes Profile Link (ക്രിക് ഹീറോസ് പ്രൊഫൈൽ ലിങ്ക്) 
                  </SoftTypography>
                  <SoftInput
                    placeholder="https://cricheroes.in/player/your-profile"
                    value={formData.cric_heroes_profile_link}
                    onChange={(e) => handleInputChange("cric_heroes_profile_link", e.target.value)}
                    icon={{
                      component: <LinkIcon />,
                      direction: "left",
                    }}
                    sx={{ height: "42px" }}
                  />
                </SoftBox>
              </Grid>
            )}

            {/* Previous Team Details - Conditionally Required */}
            {seriesData?.show_previous_team_details === "1" && (
              <Grid item xs={12}>
                <SoftBox mb={3}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                   Current Club Name (നിലവിൽ കളിക്കുന്ന ക്ലബ്‌)*
                  </SoftTypography>
                  <SoftInput
                    placeholder="e.g. Mumbai Strikers 2023, XYZ Cricket Club (ഉദാ: മുംബൈ സ്ട്രൈക്കേഴ്സ് 2023)"
                    value={formData.previous_team_details}
                    onChange={(e) => handleInputChange("previous_team_details", e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    icon={{
                      component: <SportsIcon />,
                      direction: "left",
                    }}
                  />
                  <SoftTypography
                    variant="caption"
                    color="text"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    Enter your current team or club name (max 100 characters)
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            {/* Office/Job Information */}
            {(seriesData?.show_designation === "1" || seriesData?.show_designation === 1 ||
              seriesData?.show_office_location === "1" || seriesData?.show_office_location === 1 ||
              seriesData?.show_job_type === "1" || seriesData?.show_job_type === 1 ||
              seriesData?.show_office_id_photo === "1" || seriesData?.show_office_id_photo === 1) && (
              <>
                <Grid item xs={12}>
                  <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2} mt={2}>
                    Office/Job Information (ഓഫീസ്/ജോലി വിവരങ്ങൾ)
                  </SoftTypography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Designation */}
                {(seriesData?.show_designation === "1" || seriesData?.show_designation === 1) && (
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={2}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Designation (സ്ഥാനം) *
                      </SoftTypography>
                      <SoftInput
                        placeholder="Enter your designation (സ്ഥാനം നൽകുക)"
                        value={formData.designation}
                        onChange={(e) => handleInputChange("designation", e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        sx={{ height: "42px" }}
                      />
                    </SoftBox>
                  </Grid>
                )}

                {/* Office Location */}
                {(seriesData?.show_office_location === "1" || seriesData?.show_office_location === 1) && (
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={2}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Office Location (ഓഫീസ് സ്ഥലം) *
                      </SoftTypography>
                      <SoftInput
                        placeholder="Enter office location (ഓഫീസ് സ്ഥലം നൽകുക)"
                        value={formData.office_location}
                        onChange={(e) => handleInputChange("office_location", e.target.value)}
                        inputProps={{ maxLength: 100 }}
                        sx={{ height: "42px" }}
                      />
                    </SoftBox>
                  </Grid>
                )}

                {/* Job Type */}
                {(seriesData?.show_job_type === "1" || seriesData?.show_job_type === 1) && (
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={2}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Job Type (ജോലി തരം) *
                      </SoftTypography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.job_type}
                          onChange={(e) => handleInputChange("job_type", e.target.value)}
                          displayEmpty
                          sx={{
                            height: "42px",
                            paddingRight: "32px !important",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.87)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                            "& .MuiSelect-icon": {
                              display: "block !important",
                              visibility: "visible !important",
                              opacity: "1 !important",
                              fontSize: "2rem !important",
                              color: "#000000 !important",
                              right: "8px !important",
                              top: "calc(50% - 0.5em) !important",
                              position: "absolute !important",
                              pointerEvents: "auto !important",
                              cursor: "pointer !important",
                              zIndex: 1,
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select job type</em>
                          </MenuItem>
                          {seriesData?.job_type_option_values
                            ? seriesData.job_type_option_values.split(',').map((jobType, index) => (
                                <MenuItem key={index} value={jobType.trim()}>
                                  {jobType.trim()}
                                </MenuItem>
                              ))
                            : null}
                        </Select>
                      </FormControl>
                    </SoftBox>
                  </Grid>
                )}

                {/* Office ID Photo */}
                {(seriesData?.show_office_id_photo === "1" || seriesData?.show_office_id_photo === 1) && (
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={2}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Office ID Photo (ഓഫീസ് ഐഡി ഫോട്ടോ) *
                      </SoftTypography>
                      <Box
                        sx={{
                          border: "2px dashed",
                          borderColor: "grey.300",
                          borderRadius: 2,
                          p: 2,
                          textAlign: "center",
                          cursor: "pointer",
                          "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "grey.50",
                          },
                        }}
                        onClick={() => document.getElementById("office-id-photo-input").click()}
                      >
                        <CloudUploadIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          {officeIdPhoto ? officeIdPhoto.name : "Click to upload Office ID photo"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Images only (JPEG, PNG, GIF, WebP) • Max 10MB
                        </Typography>
                      </Box>
                      {officeIdPhotoPreview && (
                        <Box mt={1} display="flex" alignItems="center" gap={1}>
                          <Box
                            component="img"
                            src={officeIdPhotoPreview}
                            alt="Office ID photo preview"
                            sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: '1px solid rgba(0,0,0,0.12)' }}
                          />
                        </Box>
                      )}
                      <input
                        id="office-id-photo-input"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileUpload("office_id_photo", e.target.files[0])}
                      />
                    </SoftBox>
                  </Grid>
                )}
              </>
            )}

            {/* Foreign Player Details */}
            {(seriesData?.show_foreign_player_details === "1" || seriesData?.show_foreign_player_details === 1) && (
              <Grid item xs={12}>
                <SoftBox mb={2}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Foreign Player (RO) <span style={{ color: "red" }}>*</span>
                  </SoftTypography>
                  <SoftInput
                    placeholder="Enter details (വിവരങ്ങൾ നൽകുക)"
                    value={formData.foreign_player_details}
                    onChange={(e) => {
                      const value = e.target.value.substring(0, 100);
                      handleInputChange("foreign_player_details", value);
                    }}
                    inputProps={{ maxLength: 100 }}
                    sx={{ height: "42px" }}
                  />
                  <SoftTypography
                    variant="caption"
                    color="text"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formData.foreign_player_details.length}/100 characters
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}

            {/* Cricket Skills */}
            {seriesData?.show_batting_style_etc === "1" && (
              <>
                <Grid item xs={12}>
                  <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2} mt={2}>
                    Cricket Skills (ക്രിക്കറ്റ് കഴിവുകൾ)
                  </SoftTypography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Role Field */}
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Role (റോൾ) *
                    </SoftTypography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.role}
                        onChange={(e) => handleInputChange("role", e.target.value)}
                        displayEmpty
                        sx={{
                          height: "42px",
                          cursor: "pointer",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                          "& .MuiSelect-select": {
                            cursor: "pointer",
                            paddingRight: "32px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            "& > input": {
                              cursor: "pointer",
                            },
                          },
                          "& .MuiSelect-icon": {
                            display: "block !important",
                            visibility: "visible !important",
                            opacity: "1 !important",
                            fontSize: "2rem !important",
                            color: "#000000 !important",
                            right: "8px !important",
                            top: "calc(50% - 0.5em) !important",
                            position: "absolute !important",
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                            zIndex: 10,
                          },
                          "& .MuiSelect-iconOpen": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select role</em>
                        </MenuItem>
                        {suggestions?.roles?.map((role, index) => (
                          <MenuItem key={index} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Batting Style (ബാറ്റിംഗ് സ്റ്റൈൽ) *
                    </SoftTypography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.batting_style}
                        onChange={(e) => handleInputChange("batting_style", e.target.value)}
                        displayEmpty
                        sx={{
                          height: "42px",
                          cursor: "pointer",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                          "& .MuiSelect-select": {
                            cursor: "pointer",
                            paddingRight: "32px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            "& > input": {
                              cursor: "pointer",
                            },
                          },
                          "& .MuiSelect-icon": {
                            display: "block !important",
                            visibility: "visible !important",
                            opacity: "1 !important",
                            fontSize: "2rem !important",
                            color: "#000000 !important",
                            right: "8px !important",
                            top: "calc(50% - 0.5em) !important",
                            position: "absolute !important",
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                            zIndex: 10,
                          },
                          "& .MuiSelect-iconOpen": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select batting style</em>
                        </MenuItem>
                        {suggestions?.batting_styles?.map((style, index) => (
                          <MenuItem key={index} value={style}>
                            {style}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Bowling Style (ബൗളിംഗ് സ്റ്റൈൽ) *
                    </SoftTypography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.bowling_style}
                        onChange={(e) => handleInputChange("bowling_style", e.target.value)}
                        displayEmpty
                        sx={{
                          height: "42px",
                          cursor: "pointer",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                          "& .MuiSelect-select": {
                            cursor: "pointer",
                            paddingRight: "32px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            "& > input": {
                              cursor: "pointer",
                            },
                          },
                          "& .MuiSelect-icon": {
                            display: "block !important",
                            visibility: "visible !important",
                            opacity: "1 !important",
                            fontSize: "2rem !important",
                            color: "#000000 !important",
                            right: "8px !important",
                            top: "calc(50% - 0.5em) !important",
                            position: "absolute !important",
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                            zIndex: 10,
                          },
                          "& .MuiSelect-iconOpen": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select bowling style</em>
                        </MenuItem>
                        {suggestions?.bowling_styles?.map((style, index) => (
                          <MenuItem key={index} value={style}>
                            {style}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SoftBox>
                </Grid>

              </>
            )}

            {/* Keeping Field - Conditionally Shown based on show_keeping_flag (Independent of Cricket Skills) */}
            {(seriesData?.show_keeping_flag === "1" || seriesData?.show_keeping_flag === 1) && (
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Can Keep Wickets? (വിക്കറ്റ് കീപ്പിംഗ് ചെയ്യാമോ?)
                  </SoftTypography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.keeping}
                      onChange={(e) => handleInputChange("keeping", e.target.value)}
                      displayEmpty
                      sx={{
                        height: "42px",
                        paddingRight: "32px !important",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.87)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1976d2",
                        },
                        "& .MuiSelect-icon": {
                          display: "block !important",
                          visibility: "visible !important",
                          opacity: "1 !important",
                          fontSize: "2rem !important",
                          color: "#000000 !important",
                          right: "8px !important",
                          top: "calc(50% - 0.5em) !important",
                          position: "absolute !important",
                          pointerEvents: "auto !important",
                          cursor: "pointer !important",
                          zIndex: 1,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select option</em>
                      </MenuItem>
                      {suggestions?.keeping_options?.map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </SoftBox>
              </Grid>
            )}

            {/* Jersey Information */}
            {seriesData?.show_jersey_no === "1" && (
              <>
                <Grid item xs={12}>
                  <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2} mt={2}>
                    Jersey Information (ജേഴ്സി വിവരങ്ങൾ)
                  </SoftTypography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Jersey Number (ജേഴ്സി നമ്പർ) *
                    </SoftTypography>
                    <SoftInput
                      placeholder="Enter preferred jersey number (ജേഴ്സി നമ്പർ നൽകുക)"
                      value={formData.jersey_no}
                      onChange={(e) => handleInputChange("jersey_no", e.target.value)}
                      icon={{
                        component: <ConfirmationNumberIcon />,
                        direction: "left",
                      }}
                      sx={{ height: "42px" }}
                    />
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Jersey Name (ജേഴ്സിയിലെ പേര്)
                    </SoftTypography>
                    <SoftInput
                      placeholder="Enter name to print on jersey (ജേഴ്സിയിൽ പ്രിന്റ് ചെയ്യേണ്ട പേര്)"
                      value={formData.jersey_name}
                      onChange={(e) => handleInputChange("jersey_name", e.target.value)}
                      sx={{ height: "42px" }}
                    />
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Jersey Size (ജേഴ്സി സൈസ്)
                    </SoftTypography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.jersey_size}
                        onChange={(e) => handleInputChange("jersey_size", e.target.value)}
                        displayEmpty
                        sx={{
                          height: "42px",
                          cursor: "pointer",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                          "& .MuiSelect-select": {
                            cursor: "pointer",
                            paddingRight: "32px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            "& > input": {
                              cursor: "pointer",
                            },
                          },
                          "& .MuiSelect-icon": {
                            display: "block !important",
                            visibility: "visible !important",
                            opacity: "1 !important",
                            fontSize: "2rem !important",
                            color: "#000000 !important",
                            right: "8px !important",
                            top: "calc(50% - 0.5em) !important",
                            position: "absolute !important",
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                            zIndex: 10,
                          },
                          "& .MuiSelect-iconOpen": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select jersey size</em>
                        </MenuItem>
                        {suggestions?.jersey_sizes?.map((size, index) => (
                          <MenuItem key={index} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SoftBox>
                </Grid>

                {(seriesData?.show_jersey_lower_size === "1" || seriesData?.show_jersey_lower_size === 1) && (
                  <Grid item xs={12} md={6}>
                    <SoftBox mb={2}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        color="dark"
                        sx={{ mb: 1, display: "block" }}
                      >
                        Jersey lower size / Pant size (പാന്റ് സൈസ്) *
                      </SoftTypography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.jersey_lower_size}
                          onChange={(e) => handleInputChange("jersey_lower_size", e.target.value)}
                          displayEmpty
                          sx={{
                            height: "42px",
                            cursor: "pointer",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "rgba(0, 0, 0, 0.87)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                            },
                            "& .MuiOutlinedInput-input": {
                              cursor: "pointer",
                            },
                            "& .MuiSelect-select": {
                              cursor: "pointer",
                              paddingRight: "32px !important",
                            },
                            "& .MuiOutlinedInput-root": {
                              cursor: "pointer",
                              "& > input": {
                                cursor: "pointer",
                              },
                            },
                            "& .MuiSelect-icon": {
                              display: "block !important",
                              visibility: "visible !important",
                              opacity: "1 !important",
                              fontSize: "2rem !important",
                              color: "#000000 !important",
                              right: "8px !important",
                              top: "calc(50% - 0.5em) !important",
                              position: "absolute !important",
                              pointerEvents: "auto !important",
                              cursor: "pointer !important",
                              zIndex: 10,
                            },
                            "& .MuiSelect-iconOpen": {
                              pointerEvents: "auto !important",
                              cursor: "pointer !important",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select pant size</em>
                          </MenuItem>
                          {suggestions?.jersey_lower_sizes?.map((size, index) => (
                            <MenuItem key={index} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SoftBox>
                  </Grid>
                )}
              </>
            )}

            {/* Jersey lower size (Pant size) only - when jersey block is hidden but pant size is required */}
            {(seriesData?.show_jersey_lower_size === "1" || seriesData?.show_jersey_lower_size === 1) &&
              seriesData?.show_jersey_no !== "1" && (
              <>
                <Grid item xs={12}>
                  <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2} mt={2}>
                    Jersey lower size / Pant size (പാന്റ് സൈസ്)
                  </SoftTypography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Pant size (പാന്റ് സൈസ്) *
                    </SoftTypography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.jersey_lower_size}
                        onChange={(e) => handleInputChange("jersey_lower_size", e.target.value)}
                        displayEmpty
                        sx={{
                          height: "42px",
                          cursor: "pointer",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1976d2",
                          },
                          "& .MuiOutlinedInput-input": {
                            cursor: "pointer",
                          },
                          "& .MuiSelect-select": {
                            cursor: "pointer",
                            paddingRight: "32px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            cursor: "pointer",
                            "& > input": {
                              cursor: "pointer",
                            },
                          },
                          "& .MuiSelect-icon": {
                            display: "block !important",
                            visibility: "visible !important",
                            opacity: "1 !important",
                            fontSize: "2rem !important",
                            color: "#000000 !important",
                            right: "8px !important",
                            top: "calc(50% - 0.5em) !important",
                            position: "absolute !important",
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                            zIndex: 10,
                          },
                          "& .MuiSelect-iconOpen": {
                            pointerEvents: "auto !important",
                            cursor: "pointer !important",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select pant size</em>
                        </MenuItem>
                        {suggestions?.jersey_lower_sizes?.map((size, index) => (
                          <MenuItem key={index} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SoftBox>
                </Grid>
              </>
            )}

            {/* File Uploads */}
            <Grid item xs={12}>
              <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2} mt={2}>
                Upload Files (ഫയലുകൾ അപ്ലോഡ് ചെയ്യുക)
              </SoftTypography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <SoftBox mb={2}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="dark"
                >
                  Profile Picture (പ്രൊഫൈൽ ചിത്രം) *
                </SoftTypography>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "primary.light",
                    },
                  }}
                  onClick={() => document.getElementById("profile-pic-input").click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: "grey.500", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {profilePic ? profilePic.name : "Click to upload profile picture"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Images only (JPEG, PNG, GIF, WebP) • Max 10MB
                  </Typography>
                </Box>
                {profilePreview && (
                  <Box mt={1} display="flex" alignItems="center" gap={1}>
                    <Box
                      component="img"
                      src={profilePreview}
                      alt="Profile preview"
                      sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: '1px solid rgba(0,0,0,0.12)' }}
                    />
                    {storedProfilePicBase64 && !profilePic && (
                      <Typography variant="caption" color="text.secondary">
                        Using saved photo (Quick Fill)
                      </Typography>
                    )}
                  </Box>
                )}
                <input
                  id="profile-pic-input"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileUpload("profile_pic", e.target.files[0])}
                />
              </SoftBox>
            </Grid>

            {seriesData?.show_gpay_screenshot === "1" && (
              <Grid item xs={12} md={6}>
                <SoftBox mb={2}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                  >
                    GPay Screenshot (GPay സ്ക്രീൻഷോട്ട്) *
                  </SoftTypography>
                  <Box
                    sx={{
                      border: "2px dashed",
                      borderColor: "grey.300",
                      borderRadius: 2,
                      p: 2,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.light",
                      },
                    }}
                    onClick={() => document.getElementById("gpay-screenshot-input").click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 40, color: "grey.500", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {gpayScreenshot ? gpayScreenshot.name : "Click to upload GPay screenshot"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Images only (JPEG, PNG, GIF, WebP) • Max 10MB
                    </Typography>
                  </Box>
                  {gpayScreenshotPreview && (
                    <Box mt={1} display="flex" alignItems="center" gap={1}>
                      <Box
                        component="img"
                        src={gpayScreenshotPreview}
                        alt="GPay screenshot preview"
                        sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: '1px solid rgba(0,0,0,0.12)' }}
                      />
                    </Box>
                  )}
                  <input
                    id="gpay-screenshot-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleFileUpload("gpay_screenshot", e.target.files[0])}
                  />
                </SoftBox>
              </Grid>
            )}

            {/* Aadhar Card Upload */}
            {seriesData?.show_aadhar_pic === "1" && (
              <>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                    >
                      Aadhar Card Front (ആധാർ കാർഡ് മുൻഭാഗം) *
                    </SoftTypography>
                    <Box
                      sx={{
                        border: "2px dashed",
                        borderColor: "grey.300",
                        borderRadius: 2,
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.light",
                        },
                      }}
                      onClick={() => document.getElementById("aadhar-front-input").click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: "grey.500", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {aadharFrontPic ? aadharFrontPic.name : "Click to upload Aadhar front"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Images only (JPEG, PNG, GIF, WebP) • Max 10MB
                      </Typography>
                    </Box>
                    {aadharFrontPreview && (
                      <Box mt={1} display="flex" alignItems="center" gap={1}>
                        <Box
                          component="img"
                          src={aadharFrontPreview}
                          alt="Aadhar front preview"
                          sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: '1px solid rgba(0,0,0,0.12)' }}
                        />
                      </Box>
                    )}
                    <input
                      id="aadhar-front-input"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileUpload("aadhar_front_pic", e.target.files[0])}
                    />
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SoftBox mb={2}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="dark"
                    >
                      Aadhar Card Back (ആധാർ കാർഡ് പിൻഭാഗം) *
                    </SoftTypography>
                    <Box
                      sx={{
                        border: "2px dashed",
                        borderColor: "grey.300",
                        borderRadius: 2,
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "primary.light",
                        },
                      }}
                      onClick={() => document.getElementById("aadhar-back-input").click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: "grey.500", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {aadharBackPic ? aadharBackPic.name : "Click to upload Aadhar back"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Images only (JPEG, PNG, GIF, WebP) • Max 10MB
                      </Typography>
                    </Box>
                    {aadharBackPreview && (
                      <Box mt={1} display="flex" alignItems="center" gap={1}>
                        <Box
                          component="img"
                          src={aadharBackPreview}
                          alt="Aadhar back preview"
                          sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover", border: '1px solid rgba(0,0,0,0.12)' }}
                        />
                      </Box>
                    )}
                    <input
                      id="aadhar-back-input"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileUpload("aadhar_back_pic", e.target.files[0])}
                    />
                  </SoftBox>
                </Grid>
              </>
            )}

            

            {/* Comments - Conditionally Shown */}
            {seriesData?.show_comments === "1" && (
              <Grid item xs={12}>
                <SoftBox mb={3}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Additional Comments (അധിക കമന്റുകൾ) (Optional)
                  </SoftTypography>
                  <SoftInput
                    placeholder="Any other comments... (മറ്റ് കമന്റുകൾ...)"
                    value={formData.comments}
                    onChange={(e) => handleInputChange("comments", e.target.value)}
                    multiline
                    rows={3}
                    icon={{
                      component: <CommentIcon />,
                      direction: "left",
                    }}
                  />
                </SoftBox>
              </Grid>
            )}

            {/* Error Message - Display near submit button */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <SoftButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading || isClosed}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {isClosed 
                    ? "Registration Closed (രജിസ്ട്രേഷൻ അവസാനിച്ചു)" 
                    : loading 
                      ? "Registering... (രജിസ്റ്റർ ചെയ്യുന്നു...)" 
                      : "Register Player (പ്ലേയർ രജിസ്റ്റർ ചെയ്യുക)"}
                </SoftButton>

                <SoftButton
                  type="button"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={handleClearForm}
                  disabled={loading}
                >
                  Clear Form (ഫോം ക്ലിയർ ചെയ്യുക)
                </SoftButton>
              </Box>
            </Grid>
          </Grid>
        </form>
        </Box>
      </CardContent>
    </Card>
  );
}

export default RegistrationForm;
